import React from 'react';
import { useHistory } from 'react-router-dom';
import { NavbarLink } from '../../../types/navbar.type';
import { AppRoutes } from '../../../routes';
import { TranslationUtils, ValidationUtils } from '../../../utils';
import { companyLinks } from '../links';
import { AdjustmentConfig, Splits } from '../../../types/split.type';
import { Reservation, Unit } from 'guest-data-types-ts-lib';
import { isFeatureAvailableForChannel, useFeatureConfig } from '../../../services/optimizely/optimizelyHelper';

interface LeftMobileNavProps {
    extend_stay_eligible?: boolean;
    add_pet_adjustment?: boolean;
    unit?: Unit;
    guestName: string | undefined;
    reservationNotifications: number;
    reservationSourceId?: number | null;
    tollFreePhoneNumber: string;
    localPhoneNumber: string;
    confirmationCode?: string;
    onLeftMenuClick: () => void;
}

export const LeftMobileNavMenu: React.FC<LeftMobileNavProps> = (props) => {
    const { reservationNotifications, confirmationCode, guestName, onLeftMenuClick, localPhoneNumber, tollFreePhoneNumber } = props;
    const vacasaUrl = ValidationUtils.validateAndGetEnvVar('REACT_APP_VACASA_URI');

    const {
        isEnabled: isManagePetsAvailable,
        config: managePetsConfig,
        clientReady: managePetClientReady,
    } = useFeatureConfig<AdjustmentConfig>(Splits.GUEST_PORTAL_NEW_PET_ADJUSTMENT, false);

    const tmpReservation = { reservation_source_id: props?.reservationSourceId } as Reservation;
    const isNewAddPetAvailable = isFeatureAvailableForChannel(
        tmpReservation,
        managePetClientReady,
        managePetsConfig,
        isManagePetsAvailable
    );
    const isExtendedStayAllowed = props.extend_stay_eligible;
    const isPetAdjustmentAllowed = props.add_pet_adjustment && isManagePetsAvailable && isNewAddPetAvailable;

    const history = useHistory();

    const handleNavigation = (path: string) => {
        onLeftMenuClick();
        history.push(path);
    };

    const renderLink = (link: NavbarLink) => {
        return (
            <a target="_blank" rel="noopener noreferrer" href={link.route} id={`${link.id}-mobile`} className="sidenavbar-item">
                <span>{TranslationUtils.get(link.displayText)}</span>
            </a>
        );
    };

    const links: NavbarLink[] = [
        {
            id: 'mobile-link-reservation',
            route: AppRoutes.RESERVATION,
            displayText: 'trip_overview_capitalized',
            notifications: reservationNotifications,
        },
        {
            id: 'mobile-link-modify-trip',
            route: AppRoutes.MODIFY_TRIP,
            displayText: 'manage_guests_capitalized',
            notifications: 0,
            indented: true,
        },
        {
            id: 'mobile-link-view-receipt',
            route: AppRoutes.RECEIPT,
            displayText: 'view_receipt_capitalized',
            notifications: 0,
            indented: true,
        },
        {
            id: 'mobile-link-cancel-your-trip',
            route: AppRoutes.CANCEL,
            displayText: 'cancel_trip_title',
            notifications: 0,
            indented: true,
        },

        {
            id: 'mobile-link-getting-there',
            route: AppRoutes.GETTING_THERE,
            displayText: 'getting_there_capitalized',
            notifications: 0,
        },
        {
            id: 'mobile-link-in-the-home',
            route: AppRoutes.IN_THE_HOME,
            displayText: 'features_and_amenities_capitalized',
            notifications: 0,
        },
        {
            id: 'mobile-link-checkout-instructions',
            route: AppRoutes.CHECKOUT_INSTRUCTIONS,
            displayText: 'checkout_instructions_capitalized',
            notifications: 0,
        },
    ];

    if (isExtendedStayAllowed) {
        links.splice(1, 0, {
            id: 'mobile-link-extended-stay',
            route: AppRoutes.EXTENDED_STAY,
            displayText: 'extended_stay_capitalized',
            notifications: 0,
            indented: true,
        });
    }

    if (isPetAdjustmentAllowed) {
        links.splice(3, 0, {
            id: 'mobile-link-manage-pets',
            route: AppRoutes.MODIFY_PETS,
            displayText: 'manage_pets_title',
            notifications: 0,
            indented: true,
        });
    }

    return (
        <div id="sidenavbar-left" className="sidenavbar left-inactive fixed-top d-lg-none">
            <div className="sidenavbar-content flex-column nav-font-mobile pt-3">
                <div className="user-mobile-bg">
                    <div className="mx-2 mt-2 pl-2">
                        <div className="my-trip">{TranslationUtils.get('my_trip')}</div>
                        <div>
                            <b>{guestName ? guestName : ''}</b>
                        </div>
                        <div>
                            {TranslationUtils.get('confirmation')} : <b>{confirmationCode ? confirmationCode : ''}</b>
                        </div>
                    </div>
                    <div className="sidenavbar-divider" />
                    <div className="mx-2">
                        {links.map((link, index) => {
                            return (
                                <a
                                    key={index}
                                    id={link.id}
                                    className={`sidenavbar-item ${link.indented ? 'mx-32' : ''}`}
                                    onClick={() => handleNavigation(link.route)}
                                >
                                    <span>{TranslationUtils.get(link.displayText)}</span>
                                    {link.notifications > 0 ? (
                                        <div className="side-notification mobile-notification-margin">{link.notifications}</div>
                                    ) : (
                                        <React.Fragment />
                                    )}
                                </a>
                            );
                        })}
                    </div>
                </div>
                <div className="sidenavbar-divider mt-0" />
                <div className="left-panel-margin">
                    <div className="mx-2">
                        {renderLink({
                            id: 'navbar-property-management',
                            route: `${vacasaUrl}/property-management`,
                            displayText: 'property_management',
                            notifications: 0,
                        })}
                        {renderLink({
                            id: 'navbar-real-estate',
                            route: `${vacasaUrl}/real-estate`,
                            displayText: 'real_estate',
                            notifications: 0,
                        })}
                    </div>
                    <div className="sidenavbar-divider" />
                    <div className="mx-3">{TranslationUtils.get('company')}</div>
                    <div className="mx-32">
                        {companyLinks.map((link, index) => {
                            return <React.Fragment key={index}>{renderLink(link)}</React.Fragment>;
                        })}
                    </div>
                    <div className="sidenavbar-divider" />
                    <div className="mx-3">{TranslationUtils.get('help')}</div>
                    <div className="mx-32">
                        <a
                            target="_blank"
                            rel="noopener noreferrer"
                            href={`${vacasaUrl}/support`}
                            id="navbar-support-mobile"
                            className="sidenavbar-item"
                        >
                            <span>{TranslationUtils.get('support_contact')}</span>
                        </a>
                        <a id="navbar-tollfree-phone-mobile" className="sidenavbar-item" href={`tel:${tollFreePhoneNumber}`}>
                            <span>{`${TranslationUtils.get('toll_free')} ${tollFreePhoneNumber}`}</span>
                        </a>
                        <a id="navbar-local-phone-mobile" className="sidenavbar-item" href={`tel:${localPhoneNumber}`}>
                            <span>{`${TranslationUtils.get('local')} ${localPhoneNumber}`}</span>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};
